// The "Add or edit" card on Resources and files (AdminResources.dc.html's aside): the kind, the title
// and its line, the file, address or text, the section, when members get it, yours for life, mark it
// new (which puts it in the bell where the product speaks: bell, from hub.bell_will_ring, Adam, 5 Oct,
// stage 6's batch), and the other products it's also shown in; the board's update email is gone (Adam,
// 5 Oct, stage 6: no notification emails). At laptop width it's the card beside the sections;
// narrower, the same fields in a dialog (Resources.jsx).
import { useRef } from 'react'
import { KINDS, KIND_LABEL, ACCEPT, vimeoNote, capitalise, shortName } from './model.js'

function Pick({ on, onClick, children, disabled, className = '', ...rest }) {
  return (
    <button type="button" className={`ar-pick ${className}`.trim()} aria-pressed={Boolean(on)} disabled={disabled} onClick={onClick} {...rest}>
      {children}
    </button>
  )
}

function Group({ label, children, note, id }) {
  return (
    <div className="ar-ed-group" role="group" aria-labelledby={id}>
      <span className="ar-ed-label" id={id}>{label}</span>
      {children}
      {note}
    </div>
  )
}

export function EditorFields({ mode, draft, setDraft, sections, weeks, others, fileName, bell = false, idPrefix = 'ar-ed' }) {
  const fileInput = useRef(null)
  const set = patch => setDraft(d => ({ ...d, ...patch }))
  const needsFile = draft.kind === 'file' || draft.kind === 'tool'
  const needsLink = draft.kind === 'link' || draft.kind === 'video'
  const chosen = draft.file?.name || fileName || null
  const relOptions = [['now', 'Straight away']]
    .concat(weeks.length ? [['after_session', 'After a session']] : [])
    .concat([['on_date', 'On a date']])

  return (
    <>
      <Group label="Kind" id={`${idPrefix}-kind`}
        note={mode === 'edit' ? <span className="ar-ed-note">To change the kind, add it again as a new item.</span> : null}>
        <div className="ar-picks">
          {KINDS.map(k => (
            <Pick key={k} on={draft.kind === k} disabled={mode === 'edit' && draft.kind !== k}
              onClick={() => set({ kind: k, file: null })}>{KIND_LABEL[k]}</Pick>
          ))}
        </div>
      </Group>

      <label className="ar-ed-label ar-ed-field">Title
        <input className="ar-input ar-input--title" value={draft.title} maxLength={160} placeholder="What members see"
          onChange={e => set({ title: e.target.value })} />
      </label>
      <label className="ar-ed-label ar-ed-field">Line under the title
        <input className="ar-input" value={draft.subline} maxLength={200} placeholder="e.g. Fillable PDF"
          onChange={e => set({ subline: e.target.value })} />
      </label>

      {needsFile && (
        <Group label={draft.kind === 'tool' ? 'The tool, as an HTML file' : 'File'} id={`${idPrefix}-file`}
          note={mode === 'edit' && fileName ? (
            <span className="ar-ed-note">Replacing keeps the same link, so nothing members saved breaks. The old version is kept, in case you need it back.</span>
          ) : null}>
          <div className="ar-filebox">
            <span className="ar-filebox-line">{chosen || 'Nothing chosen yet'}</span>
            <button type="button" className="ar-btn ar-btn--line ar-btn--xs" onClick={() => fileInput.current?.click()}>
              {chosen ? 'Replace' : 'Choose a file'}
            </button>
            <input ref={fileInput} type="file" className="ar-file-input" tabIndex={-1} aria-hidden="true" accept={ACCEPT[draft.kind]}
              onChange={e => { const f = e.target.files?.[0] || null; e.target.value = ''; if (f) set({ file: f }) }} />
          </div>
        </Group>
      )}

      {needsLink && (
        <>
          <label className="ar-ed-label ar-ed-field">{draft.kind === 'video' ? 'Vimeo link or embed code' : 'Address'}
            <input className="ar-input ar-input--small" value={draft.link} maxLength={2000}
              placeholder={draft.kind === 'video' ? 'https://vimeo.com/...' : 'https://...'}
              onChange={e => set({ link: e.target.value })} />
          </label>
          <span className="ar-ed-note ar-ed-note--tight">{draft.kind === 'video' ? vimeoNote(draft.link) : 'Opens in a new tab.'}</span>
        </>
      )}

      {draft.kind === 'text' && (
        <label className="ar-ed-label ar-ed-field">Text members copy
          <textarea className="ar-input ar-textarea" value={draft.text} maxLength={20000} placeholder="Paste the prompt or script"
            onChange={e => set({ text: e.target.value })} />
        </label>
      )}

      <Group label="Section" id={`${idPrefix}-section`}>
        <div className="ar-picks">
          {sections.map(s => <Pick key={s.id} on={draft.sectionId === s.id} className="ar-pick--sm" onClick={() => set({ sectionId: s.id })}>{s.name}</Pick>)}
        </div>
      </Group>

      <Group label="When members get it" id={`${idPrefix}-when`}>
        <div className="ar-picks">
          {relOptions.map(([value, label]) => (
            <Pick key={value} on={draft.release === value} className="ar-pick--sm" onClick={() => set({ release: value })}>{label}</Pick>
          ))}
        </div>
        {draft.release === 'after_session' && (
          <div className="ar-picks ar-weeks" role="group" aria-label="Which week's session">
            {weeks.map(w => (
              <Pick key={w.id} on={draft.moduleId === w.id} className="ar-pick--week" aria-label={`After the ${w.label} session`}
                onClick={() => set({ moduleId: w.id })}>{`W${w.number}`}</Pick>
            ))}
          </div>
        )}
        {draft.release === 'on_date' && (
          <input type="date" className="ar-input ar-input--date" aria-label="Date it opens" value={draft.date || ''}
            onChange={e => set({ date: e.target.value })} />
        )}
      </Group>

      <div className="ar-checks">
        <label className="ar-check">
          <input type="checkbox" checked={Boolean(draft.life)} onChange={() => set({ life: !draft.life })} />
          <span><b>Yours for life.</b> It stays in their library after their access to the product ends.</span>
        </label>
        <label className="ar-check">
          <input type="checkbox" checked={Boolean(draft.isNew)} onChange={() => set({ isNew: !draft.isNew })} />
          <span><b>Mark it new</b> for 14 days{bell ? ', and put it in the bell.' : '.'}</span>
        </label>
      </div>

      {others.length > 0 && (
        <Group label="Also show it in" id={`${idPrefix}-also`}
          note={<span className="ar-ed-note">It's 1 file, so replacing it updates every product it's in.</span>}>
          <div className="ar-picks">
            {others.map(p => {
              const on = draft.also.includes(p.id)
              return (
                <Pick key={p.id} on={on} className="ar-pick--also"
                  onClick={() => set({ also: on ? draft.also.filter(x => x !== p.id) : [...draft.also, p.id] })}>
                  <span className="ar-dot ar-dot--sm" style={{ background: p.dot }} aria-hidden="true" />{capitalise(shortName(p))}
                </Pick>
              )
            })}
          </div>
        </Group>
      )}
    </>
  )
}

// The card itself, at laptop width
export function EditorCard({ heading, onClose, children, footer, problem }) {
  return (
    <section className="ar-card ar-editor" aria-labelledby="ar-editor-h">
      <div className="ar-editor-head">
        <h2 className="ar-editor-title" id="ar-editor-h">{heading}</h2>
        <button type="button" className="ar-btn ar-btn--line ar-btn--xs" onClick={onClose}>Close</button>
      </div>
      {children}
      {problem && <p className="ar-problem" role="alert">{problem}</p>}
      <div className="ar-editor-foot">{footer}</div>
    </section>
  )
}
